import { writeFile, mkdir } from 'node:fs/promises';
import { join } from 'node:path';
import chalk from 'chalk';
import ora from 'ora';
import type { Preset, Level } from '../../types/index.js';
import { getPreset } from '../../presets/index.js';
import { detectStack } from '../../engine/stack-detector.js';
import { runBuiltinChecks } from '../../tools/builtin.js';

interface InitOptions {
  preset?: string;
  level: string;
  hooks: boolean;
  ci: boolean;
}

export async function initCommand(options: InitOptions): Promise<void> {
  const cwd = process.cwd();

  console.log(chalk.bold.cyan('\n🛤️  Devrail - Security & Quality Guardrails\n'));

  const spinner = ora('Detecting your stack...').start();

  try {
    // Auto-detect stack if no preset specified
    const detected = await detectStack(cwd);
    const preset = (options.preset as Preset) ?? detected.preset;
    const level = options.level as Level;

    spinner.succeed(chalk.green('Stack detected!'));

    // Show detection results
    console.log('\n' + chalk.bold('📦 Project Analysis:'));
    console.log(`  Language:    ${chalk.cyan(detected.language)}`);
    if (detected.framework) {
      console.log(`  Framework:   ${chalk.cyan(detected.framework)}`);
    }
    console.log(`  Preset:      ${chalk.cyan(preset)} ${detected.confidence < 80 ? chalk.dim('(suggested)') : ''}`);
    console.log(`  TypeScript:  ${detected.hasTypeScript ? chalk.green('✓') : chalk.dim('✗')}`);
    console.log(`  Tests:       ${detected.hasTests ? chalk.green('✓') : chalk.yellow('✗ missing')}`);
    console.log(`  CI/CD:       ${detected.hasCi ? chalk.green('✓') : chalk.yellow('✗ missing')}`);

    // Quick scan for "wow moment"
    spinner.start('Running quick security scan...');
    const quickResults = await runBuiltinChecks(cwd, [
      'secrets.gitignore-required',
      'secrets.no-env-commit',
      'deps.lockfile.required',
      'deps.no-unpinned',
      'tests.unit.required',
      'code.strict-mode',
      'config.node-version',
    ]);
    spinner.stop();

    // Show issues found (the "wow moment")
    const errors = quickResults.filter((r) => r.severity === 'error');
    const warnings = quickResults.filter((r) => r.severity === 'warn');

    if (quickResults.length > 0 || detected.issues.length > 0) {
      console.log('\n' + chalk.bold.yellow('⚠️  Issues Found:'));

      for (const issue of detected.issues) {
        console.log(`  ${chalk.red('●')} ${issue}`);
      }
      for (const result of errors) {
        console.log(`  ${chalk.red('●')} ${result.message}`);
      }
      for (const result of warnings.slice(0, 5)) {
        console.log(`  ${chalk.yellow('●')} ${result.message}`);
      }
      if (warnings.length > 5) {
        console.log(chalk.dim(`    ... and ${warnings.length - 5} more warnings`));
      }
    }

    // Create config
    spinner.start('Creating configuration...');
    await createConfigFile(cwd, preset, level);

    if (options.hooks) {
      spinner.text = 'Setting up git hooks...';
      await setupGitHooks(cwd);
    }

    if (options.ci && !detected.hasCi) {
      spinner.text = 'Creating CI templates...';
      await createCITemplates(cwd, preset, level);
    }

    await updateGitignore(cwd);
    spinner.succeed(chalk.green('Devrail initialized!'));

    // Show next actions (max 3)
    const presetConfig = getPreset(preset);
    console.log('\n' + chalk.bold('🚀 Next Actions:'));

    if (errors.length > 0 || warnings.length > 0) {
      console.log(`  1. ${chalk.cyan('npx devrail fix')}     Fix ${errors.length + Math.min(warnings.length, 5)} auto-fixable issues`);
      console.log(`  2. ${chalk.cyan('npx devrail baseline')} Accept existing issues, block new ones`);
      console.log(`  3. ${chalk.cyan('npx devrail ci')}      Run full CI check`);
    } else {
      console.log(`  1. ${chalk.cyan('npx devrail check')}  Run security scan`);
      console.log(`  2. ${chalk.cyan('npx devrail ci')}     Run full CI check`);
    }

    console.log('\n' + chalk.dim(`Config: devrail.config.yaml | ${presetConfig.rules[level]?.length ?? 0} rules active\n`));
  } catch (error) {
    spinner.fail(chalk.red('Initialization failed'));
    console.error(error);
    process.exit(1);
  }
}

async function createConfigFile(cwd: string, preset: Preset, level: Level): Promise<void> {
  const config = `# Devrail Configuration
# Docs: https://devrail.dev/docs/config

devrail:
  preset: "${preset}"
  level: "${level}"

  # Baseline: accept existing issues, block new ones
  baseline: ".devrail/baseline.json"

  # Override specific rules (optional)
  # rules:
  #   secrets.no-plaintext:
  #     enabled: true
  #     severity: error
  #   deps.no-unpinned:
  #     enabled: false

  # CI configuration
  ci:
    failOn: error
    # Only fail on NEW issues (not baselined)
    newOnly: true
    reportFormat: console

  # Tool toggles
  tools:
    eslint: true
    semgrep: true
    gitleaks: true
    osvScanner: true
`;

  await writeFile(join(cwd, 'devrail.config.yaml'), config, 'utf-8');
}

async function setupGitHooks(cwd: string): Promise<void> {
  const hooksDir = join(cwd, '.husky');

  try {
    await mkdir(hooksDir, { recursive: true });

    const preCommitHook = `#!/usr/bin/env sh
. "$(dirname -- "$0")/_/husky.sh"

npx devrail check --changed
`;

    await writeFile(join(hooksDir, 'pre-commit'), preCommitHook, { mode: 0o755 });

    const prePushHook = `#!/usr/bin/env sh
. "$(dirname -- "$0")/_/husky.sh"

npx devrail ci
`;

    await writeFile(join(hooksDir, 'pre-push'), prePushHook, { mode: 0o755 });
  } catch {
    // Husky might not be set up, create simple git hooks
    const gitHooksDir = join(cwd, '.git', 'hooks');

    try {
      const preCommitHook = `#!/usr/bin/env sh
npx devrail check --changed
`;
      await writeFile(join(gitHooksDir, 'pre-commit'), preCommitHook, { mode: 0o755 });
    } catch {
      // Git hooks dir might not exist, skip
    }
  }
}

async function createCITemplates(cwd: string, preset: Preset, level: Level): Promise<void> {
  const githubDir = join(cwd, '.github', 'workflows');
  await mkdir(githubDir, { recursive: true });

  const workflow = `name: VibeGuard Security & Quality

on:
  push:
    branches: [main, master]
  pull_request:
    branches: [main, master]

jobs:
  vibeguard:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4

      - name: Setup Node.js
        uses: actions/setup-node@v4
        with:
          node-version: '20'
          cache: 'npm'

      - name: Install dependencies
        run: npm ci

      - name: Install security tools
        run: |
          # Install gitleaks
          curl -sSfL https://github.com/gitleaks/gitleaks/releases/download/v8.18.0/gitleaks_8.18.0_linux_x64.tar.gz | tar -xz
          sudo mv gitleaks /usr/local/bin/

          # Install osv-scanner
          curl -sSfL https://github.com/google/osv-scanner/releases/download/v1.7.0/osv-scanner_linux_amd64 -o osv-scanner
          chmod +x osv-scanner
          sudo mv osv-scanner /usr/local/bin/

          # Install semgrep
          pip install semgrep

      - name: Run VibeGuard CI
        run: npx vg ci --format sarif > vibeguard-results.sarif
        continue-on-error: true

      - name: Upload SARIF results
        uses: github/codeql-action/upload-sarif@v3
        with:
          sarif_file: vibeguard-results.sarif
        if: always()

      - name: Run VibeGuard CI (blocking)
        run: npx vg ci --fail-on error
`;

  await writeFile(join(githubDir, 'vibeguard.yml'), workflow, 'utf-8');
}

async function updateGitignore(cwd: string): Promise<void> {
  const gitignorePath = join(cwd, '.gitignore');
  const entries = `
# VibeGuard
.vibeguard-cache/
vibeguard-report.json
vibeguard-report.sarif
`;

  try {
    const { readFile } = await import('node:fs/promises');
    const existing = await readFile(gitignorePath, 'utf-8');
    if (!existing.includes('.vibeguard-cache')) {
      await writeFile(gitignorePath, existing + entries, 'utf-8');
    }
  } catch {
    await writeFile(gitignorePath, entries.trim() + '\n', 'utf-8');
  }
}
